import React, { useState } from 'react';
import { View, Text, StyleSheet, Alert, ScrollView, Image } from 'react-native';
import {
  Button,
  TextInput,
  useTheme,
  ActivityIndicator,
} from 'react-native-paper';
import { DatePickerModal } from 'react-native-paper-dates';
import { launchImageLibrary, launchCamera, MediaType } from 'react-native-image-picker';
import BaseBottomSheet from './BaseBottomSheet';
import DepositService from '../services/depositService';
import { CreateDepositData } from '../types/Deposit';
import { ErrorLogger } from '../utils/errorLogger';
import { useAuth } from '../context/AuthContext';

interface AddDepositBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  onDepositCreated: () => void;
  onSuccess: () => void;
}

const AddDepositBottomSheet: React.FC<AddDepositBottomSheetProps> = ({
  visible,
  onClose,
  onDepositCreated,
  onSuccess,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [depositDate, setDepositDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [receiptUri, setReceiptUri] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const resetForm = () => {
    setAmount('');
    setDepositDate(new Date());
    setReceiptUri(null);
  };

  const handleImagePicker = () => {
    Alert.alert(
      'Select Receipt',
      'Choose how you want to add the receipt',
      [
        { text: 'Camera', onPress: openCamera },
        { text: 'Gallery', onPress: openGallery },
        { text: 'Cancel', style: 'cancel' },
      ],
    );
  };

  const openCamera = () => {
    launchCamera(
      {
        mediaType: 'photo' as MediaType,
        quality: 0.8,
        maxWidth: 1000,
        maxHeight: 1000,
      },
      response => {
        if (response.assets && response.assets[0]) {
          setReceiptUri(response.assets[0].uri || null);
        }
      },
    );
  };

  const openGallery = () => {
    launchImageLibrary(
      {
        mediaType: 'photo' as MediaType,
        quality: 0.8,
        maxWidth: 1000,
        maxHeight: 1000,
      },
      response => {
        if (response.assets && response.assets[0]) {
          setReceiptUri(response.assets[0].uri || null);
        }
      },
    );
  };

  const uploadReceipt = async (depositId: string): Promise<string | null> => {
    if (!receiptUri || !user?.uid) return null;

    try {
      setUploadingReceipt(true);
      const uploadedUrl = await DepositService.uploadReceipt(
        user.uid,
        depositId,
        receiptUri,
      );
      return uploadedUrl;
    } catch (error) {
      ErrorLogger.logError(error, 'AddDepositBottomSheet.uploadReceipt');
      Alert.alert('Error', 'Failed to upload receipt. The deposit will be saved without it.');
      return null;
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleSubmit = async () => {
    if (!amount.trim()) {
      Alert.alert('Error', 'Please enter an amount');
      return;
    }

    if (!user?.uid) {
      Alert.alert('Error', 'You must be logged in to create a deposit');
      return;
    }

    const amountNumber = parseFloat(amount);
    if (isNaN(amountNumber) || amountNumber <= 0) {
      Alert.alert('Error', 'Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);

      const depositData: CreateDepositData = {
        amount: amountNumber,
        depositDate,
        status: 'pending',
      };

      // Create the deposit first
      const createdDeposit = await DepositService.createDeposit(
        user.uid,
        depositData,
      );

      console.log('Deposit created successfully:', createdDeposit);

      // Upload receipt if provided (don't block on this)
      if (receiptUri && createdDeposit.id) {
        try {
          const finalReceiptUrl = await uploadReceipt(createdDeposit.id);
          if (finalReceiptUrl) {
            await DepositService.updateDeposit(createdDeposit.id, {
              receiptUrl: finalReceiptUrl,
            });
            console.log('Receipt uploaded successfully');
          }
        } catch (receiptError) {
          console.log('Receipt upload failed, but deposit was created:', receiptError);
          // Don't fail the whole operation if receipt upload fails
        }
      }

      onDepositCreated();
      onSuccess();
      resetForm();
      onClose();
    } catch (error) {
      ErrorLogger.logError(error, 'AddDepositBottomSheet.handleSubmit');
      Alert.alert('Error', 'Failed to create deposit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <BaseBottomSheet
        visible={visible}
        onClose={handleClose}
        title="Add Deposit"
        height={0.5}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.form}>
            <TextInput
              label="Amount (UGX)"
              value={amount}
              onChangeText={setAmount}
              keyboardType="numeric"
              mode="flat"
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              error={!!amount && (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)}
            />

            <View style={styles.fieldContainer}>
              <Button
                mode="text"
                onPress={() => setShowDatePicker(true)}
                icon="calendar"
                style={styles.fullWidthButton}
                contentStyle={styles.buttonContent}
              >
                {formatDate(depositDate)}
              </Button>
            </View>

            <View style={styles.fieldContainer}>
              {receiptUri ? (
                <View style={styles.receiptPreview}>
                  <Image source={{ uri: receiptUri }} style={styles.receiptImage} />
                  <Button
                    mode="text"
                    onPress={() => setReceiptUri(null)}
                    style={styles.fullWidthButton}
                  >
                    Remove Receipt
                  </Button>
                </View>
              ) : (
                <Button
                  mode="text"
                  onPress={handleImagePicker}
                  icon="camera"
                  style={styles.fullWidthButton}
                  contentStyle={styles.buttonContent}
                >
                  Add Receipt
                </Button>
              )}
            </View>
          </View>
          {uploadingReceipt && (
            <View style={styles.uploadingContainer}>
              <ActivityIndicator size="small" color={theme.colors.primary} />
              <Text style={[styles.uploadingText, { color: theme.colors.primary }]}>
                Uploading receipt...
              </Text>
            </View>
          )}
        </ScrollView>
        {/* Save Button */}
        <View style={styles.buttonContainer}>
          <Button
            disabled={!amount.trim() || loading}
            mode="contained"
            onPress={handleSubmit}
            loading={loading}
            testID="save-button"
          >
            {loading ? 'Saving...' : 'Save Deposit'}
          </Button>
        </View>
      </BaseBottomSheet>

      <DatePickerModal
        locale="en-GB"
        mode="single"
        visible={showDatePicker}
        onDismiss={() => setShowDatePicker(false)}
        date={depositDate}
        onConfirm={(params) => {
          setShowDatePicker(false);
          if (params.date) {
            setDepositDate(params.date);
          }
        }}
      />
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 20,
  },
  form: {
    gap: 16,
  },
  input: {},
  fieldContainer: {
    gap: 8,
  },
  fieldLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
  },
  fullWidthButton: {
    width: '100%',
  },
  buttonContent: {
    justifyContent: 'flex-start',
  },
  receiptPreview: {
    alignItems: 'center',
    gap: 12,
    marginTop: 4,
  },
  receiptImage: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 8,
    resizeMode: 'cover',
    maxWidth: 300,
  },
  uploadingContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  uploadingText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
  },
  buttonContainer: {
    padding: 16,
    paddingBottom: 16,
  },
});

export default AddDepositBottomSheet;